import fs from 'node:fs/promises';
import process from 'node:process';
import { getArgValue, hasFlag, resolveAppUrl } from './auth.js';
import { apiFetch, exitOnFailure } from './api-fetch.js';
import {
  formatWorkflowJson,
  formatWorkflowMarkdown,
  type WorkflowDetailResponse,
} from './workflow-format.js';

/**
 * Whether `workflow get` should append the human-readable `URL: …` trailer.
 *
 * The trailer is a courtesy for a human at a terminal running the bare
 * `workflow get <id>` (which defaults to pretty JSON). It must NOT be emitted
 * for any explicitly requested machine/file format — `--json` / `--format json`
 * or the markdown/spec formats — because the trailing non-JSON line breaks
 * downstream parsers piping stdout.
 */
export function shouldPrintWorkflowUrlTrailer(argv: string[]): boolean {
  const jsonOutput = hasFlag(argv, '--json') || getArgValue(argv, '--format') === 'json';
  const markdownOutput =
    hasFlag(argv, '--markdown') ||
    getArgValue(argv, '--format') === 'markdown' ||
    getArgValue(argv, '--format') === 'spec';
  return !jsonOutput && !markdownOutput;
}

export async function handleWorkflowGet(
  argv: string[],
  apiUrl: string,
  token: string
): Promise<void> {
  const workflowId = argv[0];
  if (!workflowId || workflowId.startsWith('--')) {
    console.error('Error: Missing workflow ID.');
    console.error('Usage: canary workflow get <workflowId>');
    process.exit(1);
  }

  const markdownOutput =
    hasFlag(argv, '--markdown') ||
    getArgValue(argv, '--format') === 'markdown' ||
    getArgValue(argv, '--format') === 'spec';
  const outputFile = getArgValue(argv, '--output');

  const apiRes = await apiFetch(`${apiUrl}/workflows/${workflowId}`, {
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    token,
  });
  if (!apiRes.ok) exitOnFailure(apiRes.failure);
  const result = (await apiRes.response.json()) as WorkflowDetailResponse;

  if (result.error === 'WORKFLOW_NOT_FOUND') {
    console.error('Error: Workflow not found.');
    process.exit(1);
  }
  if (result.error === 'WORKFLOW_WRONG_ORG') {
    console.error(
      `Error: Workflow belongs to another org: ${result.targetOrgName ?? result.targetOrgId}`
    );
    process.exit(1);
  }
  if (!result.workflow) {
    console.error(`Error: ${result.error ?? 'Unexpected response'}`);
    process.exit(1);
  }

  const workflow = result.workflow;
  let output: string;

  if (markdownOutput) {
    output = formatWorkflowMarkdown(workflow);
  } else {
    // Default to JSON for agent-friendly output
    output = JSON.stringify(formatWorkflowJson(workflow), null, 2);
  }

  if (outputFile) {
    try {
      await fs.writeFile(outputFile, output, 'utf-8');
      console.log(`Written to ${outputFile}`);
    } catch (err) {
      console.error(
        `Error writing to ${outputFile}: ${err instanceof Error ? err.message : String(err)}`
      );
      process.exit(1);
    }
    return;
  }

  console.log(output);

  if (shouldPrintWorkflowUrlTrailer(argv)) {
    const appUrl = resolveAppUrl(apiUrl);
    console.log(`\nURL: ${appUrl}/flows/${workflowId}`);
  }
}
